import { useMutation, useQuery } from '@apollo/client/react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AccessNotice } from '@/components/AccessNotice/AccessNotice';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Icon } from '@/components/Icon/Icon';
import { LenderForm, type LenderDraft } from '@/components/LenderForm/LenderForm';
import { LenderList } from '@/components/LenderList/LenderList';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { Select } from '@/components/Select/Select';
import { Spinner } from '@/components/Spinner/Spinner';
import { TextField } from '@/components/TextField/TextField';
import {
  ALL_LENDER_STATUSES,
  CREATE_LENDER,
  LENDERS_QUERY,
  type LendersQueryResult,
  type LenderStatus,
} from '@/graphql/lenders';
import { friendlyError, isPermissionDenied } from '@/lib/keystone-errors';
import { countryName } from '@/lib/lenders';
import { useResourceAccess } from '@/lib/use-resource-access';
import { useToast } from '@/toast/useToast';

import styles from './LendersContainer.module.css';

type StatusFilter = 'all' | LenderStatus;

const STATUS_OPTIONS: readonly (readonly [StatusFilter, string])[] = [
  ['all', 'All lenders'],
  ['ACTIVE', 'Active only'],
  ['INACTIVE', 'Inactive only'],
];

/**
 * `/lenders`: every lender, grouped by country, for anyone holding `lenders`
 * Read. Full adds the controls that change them. Inactive lenders are listed
 * too, since this is the one place they can be found and reactivated.
 */
export function LendersContainer() {
  const toast = useToast();
  const navigate = useNavigate();
  const { access, refetch: recheckAccess } = useResourceAccess('lenders');
  const canRead = access === 'read' || access === 'full';
  const canEdit = access === 'full';

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<LendersQueryResult>(LENDERS_QUERY, {
    variables: { statuses: ALL_LENDER_STATUSES },
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });
  const [createLender, { loading: saving }] = useMutation(CREATE_LENDER);

  const lenders = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (data?.lenders ?? []).filter(
      (lender) =>
        (status === 'all' || lender.status === status) &&
        (!needle ||
          [lender.name, lender.key, countryName(lender.country)].some((value) =>
            value?.toLowerCase().includes(needle),
          )),
    );
  }, [data, search, status]);

  if (!canRead) {
    return <AccessNotice area="lenders" onRetry={recheckAccess} state={access as 'checking'} />;
  }

  const onCreate = async (draft: LenderDraft) => {
    setCreateError(null);
    try {
      await createLender({ variables: draft });
      setCreating(false);
      toast.success({ title: `${draft.name} added`, description: 'Now add their documents.' });
      await refetch();
      navigate(`/lenders/${draft.key}`);
    } catch (cause) {
      setCreateError(friendlyError(cause));
      if (isPermissionDenied(cause)) {
        recheckAccess();
      }
    }
  };

  const total = data?.lenders.length ?? 0;
  const filtered = search.trim() !== '' || status !== 'all';

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          canEdit ? (
            <Button icon={<Icon name="plus" />} onClick={() => setCreating(true)}>
              Add lender
            </Button>
          ) : null
        }
        lede="The lenders LendWell works with, and the documents clients are given for each."
        title="Lenders"
      />

      {access === 'read' ? (
        <div className={styles.notice}>
          <Callout title="You can view lenders, but not change them">
            Ask an admin for full access to lenders if you need to add or update documents.
          </Callout>
        </div>
      ) : null}

      {total > 0 ? (
        <div className={styles.filters}>
          <div className={styles.search}>
            <TextField
              label="Search"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Lender name or country"
              type="search"
              value={search}
            />
          </div>
          <Select
            label="Show"
            onChange={(value) => setStatus(value as StatusFilter)}
            options={STATUS_OPTIONS}
            value={status}
          />
        </div>
      ) : null}

      {error && !data ? (
        <Callout tone="danger" title="Lenders couldn't be loaded">
          {friendlyError(error)}
        </Callout>
      ) : loading && !data ? (
        <p className={styles.loading} role="status">
          <Spinner /> Loading lenders…
        </p>
      ) : total === 0 ? (
        <EmptyState
          action={
            canEdit ? (
              <Button icon={<Icon name="plus" />} onClick={() => setCreating(true)}>
                Add lender
              </Button>
            ) : null
          }
          icon="buildings"
          title="No lenders yet"
        >
          <p>Lenders you add appear here, grouped by country.</p>
        </EmptyState>
      ) : lenders.length === 0 && filtered ? (
        <EmptyState icon="magnifying-glass" title="No lenders match">
          <p>Try a different name, or show all lenders.</p>
        </EmptyState>
      ) : (
        <LenderList
          lenders={lenders}
          renderLink={(lender, children) => <Link to={`/lenders/${lender.key}`}>{children}</Link>}
        />
      )}

      {creating ? (
        <LenderForm
          busy={saving}
          error={createError}
          mode="create"
          onCancel={() => {
            setCreating(false);
            setCreateError(null);
          }}
          onSubmit={onCreate}
        />
      ) : null}
    </div>
  );
}
